import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { UserPlus, UserX, UserCheck, Clock, ShieldCheck, ShieldX, KeyRound, Copy } from "lucide-react";
import { roleLabel } from "@/lib/roles";

const ASSIGNABLE_ROLES = ["Admin", "Authorizer", "Accounts", "ProjectManager", "MEL", "Auditor", "Trustee", "Employee"];

export default function Users() {
  const { user: current } = useAuth();
  const isAdmin = current?.role === "Admin";
  const [users, setUsers] = useState([]);
  const [pending, setPending] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [open, setOpen] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [showMissingCode, setShowMissingCode] = useState(false);
  const [rejecting, setRejecting] = useState(null); // { id, name }
  const [rejectReason, setRejectReason] = useState("");
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "Employee", designation: "", department: "", employee_code: "" });
  const [editingCode, setEditingCode] = useState(null); // { user, value, saving }
  const EMP_CODE_RE = /^RKF\d{2,4}$/;

  const canModerate = current?.role === "Admin" || current?.role === "Accounts";
  const [resetting, setResetting] = useState(null); // { user, temp_password, expires_at }
  const [resettingUser, setResettingUser] = useState(null); // user being reset (loading state)

  const openAdminReset = async (u) => {
    if (!window.confirm(`Reset password for ${u.name} (${u.email})? A temporary password will be shown once and, if email works, also emailed as a reset link.`)) return;
    setResettingUser(u.id);
    try {
      const r = await api.post(`/users/${u.id}/admin-reset`, { send_email: true });
      setResetting({ user: u, ...r.data });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reset failed");
    } finally {
      setResettingUser(null);
    }
  };
  const copyTemp = async () => {
    try {
      await navigator.clipboard.writeText(resetting.temporary_password);
      toast.success("Temporary password copied");
    } catch { toast.error("Copy failed — please select and copy manually"); }
  };

  const load = async () => {
    const [u, e] = await Promise.all([api.get("/users"), api.get("/employees").catch(() => ({ data: [] }))]);
    setUsers(u.data);
    setEmployees(e.data || []);
    if (canModerate) {
      try {
        const p = await api.get("/users/pending");
        setPending(p.data);
      } catch { setPending([]); }
    }
  };
  useEffect(() => { load(); }, [canModerate]);

  const approve = async (u) => {
    if (!window.confirm(`Authorise ${u.name} (${u.email}) to sign in?`)) return;
    try {
      await api.post(`/users/${u.id}/approve`);
      toast.success(`${u.name} approved — they can now sign in`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Approve failed"); }
  };

  const openReject = (u) => {
    setRejecting({ id: u.id, name: u.name });
    setRejectReason("");
  };
  const doReject = async () => {
    const reason = rejectReason.trim();
    if (!reason) { toast.error("Reason is required"); return; }
    try {
      await api.post(`/users/${rejecting.id}/reject`, { reason });
      toast.success(`${rejecting.name} rejected`);
      setRejecting(null);
      setRejectReason("");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Reject failed"); }
  };

  const changeRole = async (u, newRole) => {
    if (newRole === u.role) return;
    if (u.id === current?.id && newRole !== "Admin") {
      toast.error("You can't demote yourself from Super Admin — ask another Admin to do it");
      return;
    }
    if (!window.confirm(`Change ${u.name}'s role from ${roleLabel(u.role)} to ${roleLabel(newRole)}?`)) return;
    try {
      await api.patch(`/users/${u.id}`, { role: newRole });
      toast.success(`${u.name} is now ${roleLabel(newRole)}`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Role update failed"); }
  };

  // Auto-suggest employee_code from roster by matching email (exact first, then prefix)
  const emailSuggestion = React.useMemo(() => {
    const e = (form.email || "").trim().toLowerCase();
    if (!e || form.employee_code) return null;
    // Exact match on employee.email
    const exact = employees.find((emp) => (emp.email || "").toLowerCase() === e && emp.status !== "inactive");
    if (exact) return { ...exact, match: "exact email" };
    // Prefix match on local-part of email vs employee name/code
    const local = e.split("@")[0].replace(/[._-]/g, " ").trim();
    if (local.length < 3) return null;
    const prefix = employees.find((emp) => {
      if (emp.status === "inactive") return false;
      const name = (emp.name || "").toLowerCase();
      const code = (emp.employee_code || "").toLowerCase();
      return name.includes(local) || local.includes(name) || code === local;
    });
    if (prefix) return { ...prefix, match: "name from email" };
    return null;
  }, [form.email, form.employee_code, employees]);

  const applySuggestion = () => {
    if (!emailSuggestion) return;
    setForm((f) => ({
      ...f,
      employee_code: emailSuggestion.employee_code || "",
      name: f.name || emailSuggestion.name || "",
      designation: f.designation || emailSuggestion.designation || "",
      department: f.department || emailSuggestion.department || "",
    }));
    toast.success(`Linked to ${emailSuggestion.employee_code} · ${emailSuggestion.name}`);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/users", form);
      toast.success("User added");
      setOpen(false);
      setForm({ name: "", email: "", password: "", role: "Employee", designation: "", department: "", employee_code: "" });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const toggleUser = async (u) => {
    const next = u.is_active === false;
    if (!next && !window.confirm(`Disable ${u.name}? They will lose access immediately.`)) return;
    try {
      await api.patch(`/users/${u.id}`, { is_active: next });
      toast.success(next ? "Reactivated" : "Disabled");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const saveEmpCode = async () => {
    const raw = (editingCode?.value || "").trim().toUpperCase();
    if (raw && !EMP_CODE_RE.test(raw)) {
      toast.error("Employee ID must look like RKF01 or RKF247");
      return;
    }
    setEditingCode({ ...editingCode, saving: true });
    try {
      await api.patch(`/users/${editingCode.user.id}`, { employee_code: raw });
      toast.success(raw ? `Employee ID set to ${raw}` : "Employee ID cleared");
      setEditingCode(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
      setEditingCode({ ...editingCode, saving: false });
    }
  };

  const visible = users
    .filter((u) => showInactive || u.is_active !== false)
    .filter((u) => !showMissingCode || !u.employee_code);

  return (
    <div className="p-6 lg:p-8">
      <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 font-medium">Directory</div>
          <h1 className="font-display text-3xl font-extrabold mt-1">Users & Roles</h1>
          <p className="text-sm text-slate-500 mt-1">Register team members and assign roles. Disable accounts when someone leaves — they cannot log in but audit history stays intact.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="bg-[#0F172A]" data-testid="new-user-btn"><UserPlus className="w-4 h-4 mr-2" /> Register User</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Register Team Member</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Name *</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name-input" /></div>
                <div><Label>Employee Code</Label><Input value={form.employee_code} onChange={(e) => setForm({ ...form, employee_code: e.target.value })} placeholder="Optional (unique)" data-testid="user-empcode-input" /></div>
                <div className="col-span-2"><Label>Email *</Label><Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email-input" /></div>
                {emailSuggestion && (
                  <div className="col-span-2 flex items-center gap-2 p-2 rounded-md border border-blue-200 bg-blue-50 text-xs" data-testid="empcode-suggestion">
                    <span className="text-blue-900">
                      Matched employee <span className="font-mono font-semibold">{emailSuggestion.employee_code}</span> — {emailSuggestion.name}
                      <span className="ml-1 text-blue-700">({emailSuggestion.match})</span>
                    </span>
                    <Button type="button" size="sm" variant="outline" className="h-6 px-2 text-xs ml-auto" onClick={applySuggestion} data-testid="empcode-suggest-fill">
                      Fill code + details
                    </Button>
                  </div>
                )}
                <div className="col-span-2"><Label>Password *</Label><Input required type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password-input" /></div>
                <div>
                  <Label>Role</Label>
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                    <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Employee">Employee</SelectItem>
                      <SelectItem value="ProjectManager">Project Manager</SelectItem>
                      <SelectItem value="Accounts">Accounts</SelectItem>
                      <SelectItem value="Authorizer">Authorizer</SelectItem>
                      <SelectItem value="Auditor">Auditor</SelectItem>
                      <SelectItem value="Trustee">Trustee Approver</SelectItem>
                      <SelectItem value="MEL">MEL Manager</SelectItem>
                      <SelectItem value="Admin">Super Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Department</Label><Input value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} /></div>
                <div className="col-span-2"><Label>Designation</Label><Input value={form.designation} onChange={(e) => setForm({ ...form, designation: e.target.value })} /></div>
              </div>
              <p className="text-xs text-slate-500">Uniqueness is enforced on <b>email</b> and <b>employee code</b> — reusing either is rejected.</p>
              <DialogFooter><Button type="submit" data-testid="save-user-btn">Register</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex items-center gap-3 mb-3 text-xs text-slate-600">
        <label className="flex items-center gap-2"><input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} data-testid="user-show-inactive" /> Show disabled</label>
        <label className="flex items-center gap-2"><input type="checkbox" checked={showMissingCode} onChange={(e) => setShowMissingCode(e.target.checked)} data-testid="user-filter-missing-code" /> Missing EMP CODE</label>
        <span className="text-slate-400">·</span>
        <span>{visible.length} of {users.length}</span>
      </div>

      {canModerate && pending.length > 0 && (
        <Card className="mb-6 border-amber-200 bg-amber-50/40" data-testid="pending-approvals-card">
          <CardContent className="p-0">
            <div className="flex items-center gap-2 px-5 py-3 border-b border-amber-200 bg-amber-100/40">
              <Clock className="w-4 h-4 text-amber-700" />
              <h3 className="font-display text-sm font-bold text-amber-900 uppercase tracking-wider">
                Pending Approvals · {pending.length}
              </h3>
              <span className="text-xs text-amber-700/80 ml-2">
                Accounts must authorise these signups before the person can sign in.
              </span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-xs uppercase text-amber-900/70">
                    <th className="text-left px-5 py-2">Name</th>
                    <th className="text-left px-5 py-2">Email</th>
                    <th className="text-left px-5 py-2">Employee ID</th>
                    <th className="text-left px-5 py-2">Designation</th>
                    <th className="text-left px-5 py-2">Department</th>
                    <th className="text-left px-5 py-2">Requested</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {pending.map((u) => (
                    <tr key={u.id} className="border-b border-amber-100" data-testid={`pending-row-${u.id}`}>
                      <td className="px-5 py-3 font-medium">{u.name}</td>
                      <td className="px-5 py-3 text-slate-600 font-mono text-xs">{u.email}</td>
                      <td className="px-5 py-3 whitespace-nowrap">
                        <span className="font-mono text-xs">{u.employee_code || "—"}</span>
                        {u.employee_code && (
                          <span className={`ml-2 text-[10px] px-1.5 py-0.5 rounded border ${u.master_match ? (u.master_name_mismatch ? "bg-amber-100 text-amber-800 border-amber-300" : "bg-emerald-100 text-emerald-800 border-emerald-300") : "bg-slate-100 text-slate-700 border-slate-300"}`}
                                 data-testid={`pending-mastermatch-${u.id}`}>
                            {u.master_match ? (u.master_name_mismatch ? "Master: name mismatch" : "Matches master: Yes") : "Matches master: No"}
                          </span>
                        )}
                        <Button size="sm" variant="ghost" className="h-6 ml-1 px-1 text-[11px] text-blue-700" onClick={() => setEditingCode({ user: u, value: u.employee_code || "" })} data-testid={`pending-edit-code-${u.id}`}>Edit</Button>
                      </td>
                      <td className="px-5 py-3 text-slate-600">{u.designation || "—"}</td>
                      <td className="px-5 py-3 text-slate-600">{u.department || "—"}</td>
                      <td className="px-5 py-3 text-xs text-slate-500 whitespace-nowrap">{(u.created_at || "").slice(0, 10)}</td>
                      <td className="px-5 py-3 text-right whitespace-nowrap">
                        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white h-8 mr-2" onClick={() => approve(u)} data-testid={`approve-user-${u.id}`}>
                          <ShieldCheck className="w-3.5 h-3.5 mr-1" /> Approve
                        </Button>
                        <Button size="sm" variant="outline" className="h-8 border-red-300 text-red-700 hover:bg-red-50" onClick={() => openReject(u)} data-testid={`reject-user-${u.id}`}>
                          <ShieldX className="w-3.5 h-3.5 mr-1" /> Reject
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      <Dialog open={!!rejecting} onOpenChange={(o) => { if (!o) { setRejecting(null); setRejectReason(""); } }}>
        <DialogContent data-testid="reject-dialog">
          <DialogHeader><DialogTitle>Reject signup — {rejecting?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Label>Reason (shared with the person via email)</Label>
            <Input
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="e.g. Not a valid organisation email address"
              data-testid="reject-reason-input"
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setRejecting(null); setRejectReason(""); }}>Cancel</Button>
            <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={doReject} data-testid="reject-confirm-btn">
              Reject & notify
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editingCode} onOpenChange={(o) => { if (!o) setEditingCode(null); }}>
        <DialogContent data-testid="edit-emp-code-dialog">
          <DialogHeader><DialogTitle>Employee ID — {editingCode?.user?.name}</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <Label>Employee ID</Label>
            <Input
              value={editingCode?.value || ""}
              onChange={(e) => setEditingCode({ ...editingCode, value: e.target.value.toUpperCase().replace(/\s+/g, "") })}
              placeholder="RKF01 … RKF247 (blank allowed for Super Admin / consultants)"
              autoFocus
              data-testid="edit-emp-code-input"
            />
            <p className="text-[11px] text-slate-500">Format: RKF + 2 to 4 digits. Must be unique across users.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingCode(null)}>Cancel</Button>
            <Button onClick={saveEmpCode} disabled={editingCode?.saving} data-testid="edit-emp-code-save">
              {editingCode?.saving ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!resetting} onOpenChange={(o) => { if (!o) setResetting(null); }}>
        <DialogContent data-testid="admin-reset-dialog">
          <DialogHeader>
            <DialogTitle>Temporary password — {resetting?.user?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              Share this password with <b>{resetting?.user?.name}</b> privately. It works once,
              expires in 24 hours, and forces them to set a new password on next sign-in.
            </p>
            <div className="flex items-center gap-2 p-3 rounded border border-amber-300 bg-amber-50">
              <code className="flex-1 font-mono text-sm select-all" data-testid="admin-reset-temp-pw">
                {resetting?.temporary_password}
              </code>
              <Button size="sm" variant="outline" onClick={copyTemp} data-testid="admin-reset-copy-btn">
                <Copy className="w-3.5 h-3.5 mr-1" /> Copy
              </Button>
            </div>
            {resetting?.email_status === "queued" && (
              <p className="text-xs text-emerald-700">
                ✓ A reset link was also sent to <b>{resetting?.user?.email}</b>. They can use either the temp password or the link — whichever they receive first.
              </p>
            )}
            {resetting?.email_status === "failed" && (
              <p className="text-xs text-amber-700">
                Email delivery failed — share the temporary password above via another channel.
              </p>
            )}
            <p className="text-xs text-slate-500">
              Expires: {new Date(resetting?.expires_at).toLocaleString()}
            </p>
          </div>
          <DialogFooter>
            <Button onClick={() => setResetting(null)} data-testid="admin-reset-close-btn">Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-xs uppercase text-slate-500">
                <th className="text-left px-5 py-3">Name</th>
                <th className="text-left px-5 py-3">Emp Code</th>
                <th className="text-left px-5 py-3">Email</th>
                <th className="text-left px-5 py-3">Role</th>
                <th className="text-left px-5 py-3">Department</th>
                <th className="text-left px-5 py-3">Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {visible.map(u => {
                const disabled = u.is_active === false;
                const isSelf = u.id === current?.id;
                return (
                  <tr key={u.id} className={`border-b border-slate-100 ${disabled ? "opacity-60" : ""}`} data-testid={`user-row-${u.id}`}>
                    <td className="px-5 py-3 font-medium">{u.name}</td>
                    <td className="px-5 py-3 font-mono text-xs">
                      <span className="text-slate-500">{u.employee_code || "—"}</span>
                      {isAdmin && (
                        <Button size="sm" variant="ghost" className="h-5 px-1 ml-1 text-[10px] text-blue-700" onClick={() => setEditingCode({ user: u, value: u.employee_code || "" })} data-testid={`user-edit-code-${u.id}`}>Edit</Button>
                      )}
                    </td>
                    <td className="px-5 py-3 text-slate-600">{u.email}</td>
                    <td className="px-5 py-3">
                      {isAdmin && !isSelf ? (
                        <Select value={u.role} onValueChange={(v) => changeRole(u, v)}>
                          <SelectTrigger className="h-8 w-[150px] text-xs" data-testid={`role-select-${u.id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {ASSIGNABLE_ROLES.map(r => (
                              <SelectItem key={r} value={r} data-testid={`role-opt-${u.id}-${r}`}>{roleLabel(r)}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <Badge variant="outline">{roleLabel(u.role)}</Badge>
                      )}
                    </td>
                    <td className="px-5 py-3 text-slate-600">{u.department || "—"}</td>
                    <td className="px-5 py-3">
                      <Badge className={`text-[10px] ${disabled ? "bg-red-100 text-red-700" : "bg-emerald-100 text-emerald-700"}`}>{disabled ? "Disabled" : "Active"}</Badge>
                    </td>
                    <td className="px-5 py-3 text-right">
                      {!isSelf && isAdmin && (
                        <Button size="sm" variant="ghost" onClick={() => openAdminReset(u)}
                                disabled={resettingUser === u.id}
                                data-testid={`user-reset-password-${u.id}`}
                                className="text-blue-700 mr-1">
                          <KeyRound className="w-3.5 h-3.5 mr-1" />
                          {resettingUser === u.id ? "Resetting..." : "Reset password"}
                        </Button>
                      )}
                      {!isSelf && (
                        <Button size="sm" variant="ghost" onClick={() => toggleUser(u)} data-testid={`user-toggle-${u.id}`} className={disabled ? "text-emerald-700" : "text-red-600"}>
                          {disabled ? <><UserCheck className="w-3.5 h-3.5 mr-1" /> Reactivate</> : <><UserX className="w-3.5 h-3.5 mr-1" /> Disable</>}
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
